import { Component, EventEmitter, OnInit, OnDestroy, Input, Output } from '@angular/core';
import { SimpleChanges, OnChanges } from '@angular/core';

import { Events } from '../../helpers/events';

// ionic
import { AlertController, Platform } from '@ionic/angular';

// rxjs
import { Subject } from "rxjs";
import { takeUntil, debounceTime } from "rxjs/operators";

// services
import { UserService } from "../../services/user.service";
import { ParticipantService } from "../../services/participant.service";
import { FeedService } from "../../services/feed.service";
import { PlatformService } from "../../services/platform.service";
import { OverlayService } from "../../services/overlay.service";
import { ChimeService } from "../../services/chime.service";

// other services
import { TranslateService } from "@ngx-translate/core";

// models
import { Feed } from "../../models/feed";
import { Participant } from "../../models/participant";
import { User } from "../../models/user";
import { ChatGroup } from "../../models/chat-group";

import {
    AudioProfile,
    ConsoleLogger,
    DefaultDeviceController,
    DefaultMeetingSession,
    DefaultModality,
    DefaultAudioMixController,
    DefaultActiveSpeakerPolicy,
    LogLevel,
    TimeoutScheduler,
    MeetingSessionConfiguration,
    MeetingSessionStatusCode,
    DefaultBrowserBehavior,
    // DefaultMeetingReadinessChecker,
    // CheckAudioInputFeedback
} from 'amazon-chime-sdk-js';

import { log } from "../../helpers/log";

declare var cordova: any;

@Component({
    selector: 'chime',
    templateUrl: './chime.component.html',
    styleUrls: ['./chime.component.scss'],
})
export class ChimeComponent implements OnInit, OnDestroy, OnChanges {

    @Input() participant: Participant;

    @Input() chatGroup: ChatGroup;

    @Input() chatGroupAttendees: Participant[];

    @Input() external;

    @Output() onCanceled: EventEmitter<any> = new EventEmitter();

    @Output() onLoaded: EventEmitter<any> = new EventEmitter();

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * current event
     *
     * @type {User}
     */
    public user: User;

    public chimeMeetingId: number;

    public meetingSession: DefaultMeetingSession;

    // current notification for other attendee
    // to be able to clear notification when call is without response
    public notification: Feed;

    public loading: boolean = true;

    public canUnmute: boolean = true;

    @Input() public muted: boolean = false;

    @Input() public muteOnStart: boolean = false;

    @Input() public videoEnabled: boolean = false;

    @Input() public audioEnabled: boolean = true;

    // is user sharing screen?
    public contentShared: boolean = false;

    // is setting view visible
    public settingsEnabled: boolean = false;

    public testingSound: boolean = false;

    // is stream with shared screen available
    public sharedVideo;

    // no device access (audio/video) is allowed
    public noDevice: boolean = false;

    // no camera access is allowed
    public noCamera: boolean = false;

    // screen sharing functionality is not available
    public noScreenShare: boolean = false;

    // everything for chime started successfully
    public meetingStartSucceeded: boolean = false;

    // is there any incoming video stream from camera
    public videoVisible;

    // chime ID of most active speaker
    public activeSpeaker: string;

    // list of available devices
    public videoInputDevices = [];
    public audioInputDevices = [];
    public audioOutputDevices = [];

    // selected audio/video devices
    public selectedVideoInputDevice;
    public selectedAudioInputDevice;
    public selectedAudioOutputDevice;

    // ngModels for settings selects
    public audioInput;
    public audioOutput;
    public videoInput;

    // is loading visible for some device toggle button?
    public audioInputLoading;
    public audioOutputLoading;
    public videoInputLoading;

    // html video/audio elements
    public videoElements;
    public videoStreams = {};
    public streamLimits = 4;
    public videoElementSharing;
    public videoPreviewElement;
    public audioElement;

    // list of attendees in video call
    public roster = {}

    private analyserNode;
    private analyserNodeCallback = () => { };

    defaultBrowserBehaviour: DefaultBrowserBehavior = new DefaultBrowserBehavior();

    /**
     * which select type should be used.. alert/popover/action-sheet
     *
     * @type {string}
     */
    public selectType: string = 'popover';

    // list of meeting attendee IDs
    public attendeePresenceSet: Set<string> = new Set();

    public attendeeVisibleSet: Set<string> = new Set();

    public chimeParticipantPrefix = 'p';

    public reconnectTryCount: number = 6;

    // has preview started?
    public videoPreviewStarted: boolean = false;

    constructor(
        private chimeService: ChimeService,
        private alertCtrl: AlertController,
        private platform: Platform,
        protected translate: TranslateService,
        public overlayService: OverlayService,
        public feedService: FeedService,
        public userService: UserService,
        public participantService: ParticipantService,
        public plt: PlatformService,
        public appEvents: Events
    ) {
        if (this.plt.sizeSm) {
            this.selectType = 'action-sheet';
        }

        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe((user: User) => {
            this.user = user;
        });

        if (!this.plt.is('cordova')) {
            // check audio availability
            navigator.mediaDevices.getUserMedia({ audio: true }).then(
                (success) => {

                },
                (error) => {
                    this.noDevice = true;
                }
            );

            // check video availability
            navigator.mediaDevices.getUserMedia({ audio: true, video: true }).then(
                (success) => {

                },
                (error) => {
                    this.noCamera = true;
                }
            );
        }

        // check permission on cordova devices and ask for it if needed
        if (this.plt.is('cordova')) {
            this.platform.ready().then(() => {
                cordova.plugins.diagnostic.isMicrophoneAuthorized(
                    (authorized) => {
                        log('info', "App is " + (authorized ? "authorized" : "denied") + " access to the microphone");
                        if (!authorized) {
                            cordova.plugins.diagnostic.requestMicrophoneAuthorization(
                                (status) => {
                                    if (status === cordova.plugins.diagnostic.permissionStatus.GRANTED) {
                                        log('info', 'Microphone use is authorized');
                                        cordova.plugins.diagnostic.isCameraAuthorized(
                                            (authorized) => {
                                                log('info', "App is " + (authorized ? "authorized" : "denied") + " access to the camera");
                                                if (!authorized) {
                                                    cordova.plugins.diagnostic.requestCameraAuthorization(
                                                        (status) => {
                                                            log('info', "Authorization request for camera use was " + (status == cordova.plugins.diagnostic.permissionStatus.GRANTED ? "granted" : "denied"));
                                                            if (status != cordova.plugins.diagnostic.permissionStatus.GRANTED) {
                                                                this.noCamera = true;
                                                            }
                                                        }, (error) => {
                                                            log('error', "The following error occurred: " + error);
                                                        }, false
                                                    );
                                                }
                                            }, (error) => {
                                                log('error', "The following error occurred: " + error);
                                            }, false
                                        );
                                    } else {
                                        this.noDevice = true;
                                    }

                                }, (error) => {
                                    log('error', error);
                                });
                        }
                    }, (error) => {
                        log('error', "The following error occurred: " + error);
                    });
            });
        }
    }

    ngOnInit() {

        // bind html elements to attributes
        this.videoElementSharing = <HTMLVideoElement>document.getElementById('video-meeting-sharing');
        this.videoPreviewElement = <HTMLVideoElement>document.getElementById('video-preview');
        this.audioElement = <HTMLVideoElement>document.getElementById('meeting-audio');

        let subscription;

        if (this.chatGroup) {
            subscription = this.chimeService.getAllByChatGroupId(this.chatGroup.id);
        } else {
            subscription = this.chimeService.getAllByParticipantId(this.participant.id);
        }

        // load data for meeting
        subscription.subscribe(
            (success) => {
                if (!this.noDevice) {

                    this.videoElements = document.querySelectorAll('.video-meeting');

                    this.notification = success.notification;

                    this.chimeMeetingId = success.chime_meeting_id;
                    this.overlayService.waitingCalls.push(this.chimeMeetingId);
                    this.overlayService.liveCallId = this.chimeMeetingId;

                    const configuration = new MeetingSessionConfiguration(success.meeting, success.attendee);

                    const logger = new ConsoleLogger('MyLogger', LogLevel.WARN);
                    try {
                        const deviceController = new DefaultDeviceController(logger);

                        this.meetingSession = new DefaultMeetingSession(
                            configuration,
                            logger,
                            deviceController
                        );

                    } catch (e) {
                        log('error', e);
                    }

                    // attach default audio output to audio element
                    this.meetingSession.audioVideo.bindAudioElement(this.audioElement);
                    // prefer speech audio mode
                    this.meetingSession.audioVideo.setAudioProfile(AudioProfile.fullbandSpeechMono());

                    // attach lisener to attendee presence subscription
                    this.meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(
                        (
                            attendeeId: string,
                            present: boolean,
                            externalUserId: string,
                            dropped: boolean
                        ): void => {
                            log('info', `${attendeeId} present = ${present} (${externalUserId})`);
                            // const isContentAttendee = new DefaultModality(attendeeId).hasModality(
                            //     DefaultModality.MODALITY_CONTENT
                            // );
                            const isSelfAttendee =
                                new DefaultModality(attendeeId).base() ===
                                this.meetingSession.configuration.credentials.attendeeId;

                            if (!isSelfAttendee) {
                                this.meetingStartSucceeded = true;
                            }

                            // attendee has left meeting (ignore content streams - from screen sharing)
                            if (attendeeId.indexOf('#content') == -1) {

                                if (present) {
                                    // add new participant to list, but ignore user it self
                                    if (!isSelfAttendee && !this.attendeePresenceSet.has(externalUserId)) {
                                        this.tryToAddUser(externalUserId, attendeeId);
                                    }
                                } else {
                                    this.attendeePresenceSet.delete(externalUserId);
                                    delete this.roster[externalUserId];
                                    // if user was presented in main window
                                    if (this.attendeeVisibleSet.has(externalUserId)) {
                                        this.attendeeVisibleSet.delete(externalUserId);
                                        // replace removed users from roaster
                                        this.addVisibleAttendee(this.activeSpeaker == externalUserId);
                                    }

                                    if (this.attendeePresenceSet.size == 0) {
                                        // there is no other attendee in call
                                        this.meetingStartSucceeded = false;
                                    }
                                    log('info', `${attendeeId} dropped = ${dropped} (${externalUserId})`);
                                }


                                if (!present && !this.chatGroup) {
                                    this.meetingStartSucceeded = false;
                                    // this.updateRoster();
                                    log('info', `${attendeeId} dropped = ${dropped} (${externalUserId})`);
                                    return;
                                }
                            }
                            //If someone else share content, stop the current content share
                            // if (
                            //     !this.allowMaxContentShare() &&
                            //     !isSelfAttendee &&
                            //     isContentAttendee &&
                            //     this.isButtonOn('button-content-share')
                            // ) {
                            //     this.contentShareStop();
                            // }
                            // if (!this.roster[attendeeId]) {
                            //     this.roster[attendeeId] = {
                            //         name: externalUserId.split('#').slice(-1)[0] + (isContentAttendee ? ' «Content»' : ''),
                            //     };
                            // }
                            if (!isSelfAttendee) {
                                // add some delay to be sure participatn details are loaded
                                setTimeout(() => {
                                    this.meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(
                                        attendeeId,
                                        async (
                                            attendeeId: string,
                                            volume: number | null,
                                            muted: boolean | null,
                                            signalStrength: number | null
                                        ) => {
                                            if (!this.roster[externalUserId]) {
                                                return;
                                            }
                                            if (volume !== null) {
                                                this.roster[externalUserId].volume = Math.round(volume * 100);
                                            }
                                            if (muted !== null) {
                                                this.roster[externalUserId].muted = muted;
                                            }
                                            if (signalStrength !== null) {
                                                this.roster[externalUserId].signalStrength = Math.round(signalStrength * 100);
                                            }
                                        }
                                    );
                                }, 200);
                            }

                            this.publishAttendees();
                        }
                    );

                    this.meetingSession.audioVideo.subscribeToActiveSpeakerDetector(
                        new DefaultActiveSpeakerPolicy(),
                        this.activeSpeakerCallback
                    );

                    // attach chime events to app flow
                    const observer = {
                        eventDidReceive: (event, attr) => {
                            // console.info('EVENT', event);
                            // console.info('EVENT', attr);
                        },
                        // videoTileDidUpdate is called whenever a new tile is created or tileState changes.
                        videoTileDidUpdate: (tileState) => {

                            if (!tileState.boundExternalUserId) {
                                return;
                            }

                            const isSelfAttendee = this.user.selected_participant_id === parseInt(tileState.boundExternalUserId.replace(this.chimeParticipantPrefix, ''))

                            // add user id to list of active users.. this is faster then realtimeSubscribeToAttendeeIdPresence
                            if (!isSelfAttendee && !this.attendeePresenceSet.has(tileState.boundExternalUserId)) {
                                this.attendeePresenceSet.add(tileState.boundExternalUserId);
                                // load required data about user and set all details in roster
                                this.tryToAddUser(tileState.boundExternalUserId, tileState.boundAttendeeId);
                            }

                            // Ignore a tile without attendee ID and other attendee's tile.
                            if (tileState.localTile) {
                                // this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, this.videoPreviewElement);
                            } else {
                                // show only other participant stream in main window
                                // give some time to render video elements for new participants...
                                setTimeout(() => {
                                    if (tileState.isContent) {
                                        // screen sharing
                                        // stop my screen sharing if that is running
                                        if (!isSelfAttendee && this.contentShared) {
                                            this.toggleContentShare();
                                        }
                                        this.sharedVideo = tileState.tileId;
                                        this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, this.videoElementSharing);
                                    } else {
                                        // make first video active...
                                        if (!this.videoVisible) {
                                            this.videoVisible = tileState.tileId;
                                        }
                                        this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, this.acquireVideoElement(tileState));
                                    }
                                }, 200);
                            }

                        },

                        contentShareDidStop: () => {
                            this.contentShared = false;
                        },

                        // some video stream was removed
                        videoTileWasRemoved: (tileId) => {
                            // shared video was stopped
                            if (this.sharedVideo == tileId) {
                                this.sharedVideo = null;
                            } else {
                                // camera stream was stopped
                                this.releaseVideoElement(tileId);
                            }
                        },
                        // end of the meeting
                        audioVideoDidStop: sessionStatus => {
                            const sessionStatusCode = sessionStatus.statusCode();
                            if (sessionStatusCode === MeetingSessionStatusCode.MeetingEnded) {

                                this.meetingSession.audioVideo.stopAudioInput();

                                // Or use the destroy API to call stopAudioInput and stopVideoInput.
                                this.meetingSession.deviceController.destroy();
                                /*
                                  - You (or someone else) have called the DeleteMeeting API action in your server application.
                                  - You attempted to join a deleted meeting.
                                  - No audio connections are present in the meeting for more than five minutes.
                                  - Fewer than two audio connections are present in the meeting for more than 30 minutes.
                                  - Screen share viewer connections are inactive for more than 30 minutes.
                                  - The meeting time exceeds 24 hours.
                                  See https://docs.aws.amazon.com/chime/latest/dg/mtgs-sdk-mtgs.html for details.
                                */
                                log('info', 'The session has ended');
                                this.meetingEnded();
                            } else {
                                log('info', 'Stopped with a session status code: ', sessionStatusCode);
                            }
                        }
                    };

                    this.meetingSession.audioVideo.addObserver(observer);

                    // load device names (audio/video)
                    this.meetingSession.audioVideo.setDeviceLabelTrigger(
                        async (): Promise<MediaStream> => {

                            // now only audio is required [, video: true]
                            const stream = navigator.mediaDevices.getUserMedia({ audio: true });

                            return stream;
                        }
                    );

                    // this.meetingSession.audioVideo.startLocalVideoTile();

                    // check audio muting
                    this.meetingSession.audioVideo.realtimeSubscribeToMuteAndUnmuteLocalAudio((isMuted: boolean) => {
                        this.muted = isMuted;
                    });
                    this.muted = this.meetingSession.audioVideo.realtimeIsLocalAudioMuted();

                    // check if audiot can be unmuted
                    this.meetingSession.audioVideo.realtimeSubscribeToSetCanUnmuteLocalAudio((canUnmute: boolean) => {
                        this.canUnmute = canUnmute;
                        if (this.canUnmute) {
                            this.meetingSession.audioVideo.realtimeUnmuteLocalAudio();
                        }
                    });

                    // start call with selecting device
                    this.startCall();

                    this.onLoaded.emit();
                }
            },
            (error) => {
                const data = error.error;
                if (data.call_disabled) {
                    this.endCall();
                    this.overlayService.showError(data.message);
                }
            }
        );

        if (this.external) {
            // disable back button functionality
            window.addEventListener('popstate', (event) => {
                history.pushState(null, document.title, location.href);
                // NOTE[jg] mabye use?
                // window.onbeforeunload = function() { return "Your work will be lost."; };
            });
        }
    }

    // init chime meeting
    async startCall() {

        // const logger = new ConsoleLogger('MyLogger', LogLevel.INFO);

        // In the usage examples below, you will use this meetingReadinessChecker object.
        // const meetingReadinessChecker = new DefaultMeetingReadinessChecker(logger, this.meetingSession);

        // load list of available devices for audio/video (input,output)
        this.audioInputDevices = await this.meetingSession.audioVideo.listAudioInputDevices(true);

        if (this.audioInputDevices.length == 0) {
            // no device, or not allowed
            this.noDevice = true;
            this.loading = false;
        } else {

            // check and find some available audio input device
            let result;
            for (let i = 0; i < this.audioInputDevices.length; i++) {

                if (!this.selectedAudioInputDevice) {

                    try {
                        // let audioInputFeedback = await meetingReadinessChecker.checkAudioInput(this.audioInputDevices[i].deviceId);

                        // switch (audioInputFeedback) {
                        //     case CheckAudioInputFeedback.Failed:
                        //         log('info', 'Failed to check', this.audioInputDevices[i]);
                        //         // TODO[jg] - maybe show some warning? on simulator enabled for testing...
                        //     case CheckAudioInputFeedback.Succeeded:

                        this.selectedAudioInputDevice = this.audioInputDevices[i];
                        this.audioInput = this.audioInputDevices[i];
                        result = await this.meetingSession.audioVideo.startAudioInput(this.audioInputDevices[i].deviceId);

                        // break;

                        //     case CheckAudioInputFeedback.PermissionDenied:
                        //         log('info', 'Permission denied', this.audioInputDevices[i]);
                        //         break;
                        // }
                    } catch (e) {
                        // device is not available
                        // this.selectedAudioInputDevice = null
                    }
                }
            }

            if (!this.selectedAudioInputDevice) {
                this.noDevice = true;
                this.loading = false;
            }

            if (this.muteOnStart) {
                // mute microphone on start
                this.muted = true;
                setTimeout(() => {
                    this.meetingSession.audioVideo.realtimeMuteLocalAudio();
                }, 200);
            }

            try {
                this.videoInputDevices = await this.meetingSession.audioVideo.listVideoInputDevices(true);

                // check and find some available video input device
                for (let i = 0; i < this.videoInputDevices.length; i++) {
                    if (!this.selectedVideoInputDevice) {
                        try {
                            this.selectedVideoInputDevice = this.videoInputDevices[i];
                            this.videoInput = this.videoInputDevices[i];
                            result = await this.meetingSession.audioVideo.startVideoInput(this.videoInputDevices[i].deviceId);
                        } catch (e) {
                            // device is not available
                        }
                    }
                }

            } catch (e) {
            }

            try {
                this.audioOutputDevices = await this.meetingSession.audioVideo.listAudioOutputDevices(true);

                // check and set output devices
                if (this.audioOutputDevices[0]) {
                    this.selectedAudioOutputDevice = this.audioOutputDevices[0];
                    this.audioOutput = this.audioOutputDevices[0];
                    // if output devices are supported (some browsers does not support this API)
                    if (this.defaultBrowserBehaviour.supportsSetSinkId()) {
                        try {
                            await this.meetingSession.audioVideo.chooseAudioOutput(this.selectedAudioOutputDevice.deviceId);

                        } catch (e) {
                            log('error', 'failed to chooseAudioOutputDevice', e);
                        }
                    }
                }

            } catch (e) {
            }
            this.meetingSession.audioVideo.start();
            this.loading = false;
        }
    }

    // enable/disable video stream from camera
    toggleVideo(hardDisable = false) {
        if (!this.videoInputLoading) {
            this.videoInputLoading = true;
            if (this.videoEnabled || hardDisable) {
                // really disable video
                if (this.videoEnabled) {
                    this.meetingSession.audioVideo.stopLocalVideoTile();
                }

                this.videoEnabled = false;
                // Optional: You can remove the local tile from the session.
                // this.meetingSession.audioVideo.removeLocalVideoTile();
                // start again local stream for video preview
                this.meetingSession.audioVideo.startVideoInput(this.selectedVideoInputDevice.deviceId).then(
                    (info) => {
                        try {
                            // start video preview again, except ios
                            if (!this.videoPreviewStarted || this.plt.is('ios')) {
                                this.startVideoPreview();
                                this.videoPreviewStarted = true;
                            }
                            setTimeout(() => {
                                this.videoInputLoading = false;
                            }, 250);
                        } catch (e) {
                            // something got wrong, disable camera and loading
                            this.videoEnabled = false;
                            this.videoInputLoading = false;
                        }
                    }, (error) => {
                        this.videoEnabled = false;
                        this.videoInputLoading = false;
                        this.overlayService.showError(this.translate.instant('VIDEOCALL_DEVICE_ERROR', { device: this.selectedVideoInputDevice.label }))
                    }
                );
            } else {

                if (this.plt.is('ios')) {
                    this.stopVideoPreview();
                }

                // Make sure you have chosen your camera. In this use case, you will choose the first device.
                this.meetingSession.audioVideo.startVideoInput(this.selectedVideoInputDevice.deviceId).then(
                    (info) => {
                        try {
                            this.meetingSession.audioVideo.startLocalVideoTile();
                            if (!this.videoPreviewStarted || this.plt.is('ios')) {
                                this.startVideoPreview();
                                this.videoPreviewStarted = true;
                            }
                            setTimeout(() => {
                                this.videoEnabled = true;
                                this.videoInputLoading = false;
                            }, 250);
                        } catch (e) {
                            // something got wrong, disable camera and loading
                            this.videoEnabled = false;
                            this.videoInputLoading = false;
                        }
                    }, (error) => {
                        this.videoEnabled = false;
                        this.videoInputLoading = false;
                        this.overlayService.showError(this.translate.instant('VIDEOCALL_DEVICE_ERROR', { device: this.selectedVideoInputDevice.label }))
                    }
                );
            }
        }
    }

    // enable/disable audio stream from microphone
    toggleMicrophone() {
        if (!this.audioInputLoading) {
            this.audioInputLoading = true;
            this.muted = !this.muted;
            if (!this.muted) {
                // check if can be unmuted...
                if (this.canUnmute) {
                    try {
                        this.meetingSession.audioVideo.realtimeUnmuteLocalAudio();
                        setTimeout(() => {
                            this.audioInputLoading = false;
                        }, 200);
                    } catch (e) {
                        this.noDevice = true;
                        this.loading = false;
                        this.audioInputLoading = false;
                    }
                } else {
                    // TODO ?
                    setTimeout(() => {
                        this.audioInputLoading = false;
                    }, 200);
                }
            } else {
                // start local video stream
                this.meetingSession.audioVideo.realtimeMuteLocalAudio();
                setTimeout(() => {
                    this.audioInputLoading = false;
                }, 200);
            }
        }
    }

    // enable/disable audiot output
    toggleAudio() {
        if (!this.audioOutputLoading) {
            this.audioOutputLoading = true;

            if (!this.audioEnabled) {
                this.meetingSession.audioVideo.bindAudioElement(this.audioElement).then(
                    (success) => {
                        this.audioEnabled = true;
                        this.audioOutputLoading = false;
                    },
                    (errror) => {
                        this.noDevice = true;
                        this.loading = false;
                    });

            } else {
                // start local video stream
                this.meetingSession.audioVideo.unbindAudioElement();
                this.audioEnabled = false;
                setTimeout(() => {
                    this.audioOutputLoading = false;
                }, 200);
            }
        }
    }

    // show/hide settings info
    toggleSettings() {
        this.settingsEnabled = !this.settingsEnabled;
        if (this.settingsEnabled) {
            if (!this.videoEnabled && (!this.videoPreviewStarted || this.plt.is('ios'))) {
                // NOTE[jg] - stop video preview only when changing source, otherwise it is causing problems...
                if (!this.noCamera && this.selectedVideoInputDevice) {
                    // start again video preview
                    if (!this.videoEnabled) {
                        this.toggleVideo(true);
                    }
                }
            }
            this.startAudioPreview();

        } else {
            // NOTE[jg] - this code is not needed as video preview is still running
            // stop preview, start camera
            if (!this.noCamera) {
                // this.stopVideoPreview();

                // start cemara
                if (!this.videoEnabled) {
                    // NOTE[jg] - stop video preview only when changing source, otherwise it is causing problems...
                    // this.stopVideoPreview();
                    // this.meetingSession.audioVideo.chooseVideoInputDevice(this.selectedVideoInputDevice.deviceId).then(
                    //     (info) => {
                    //
                    //         // this.meetingSession.audioVideo.startLocalVideoTile();
                    //     }, (error) => {
                    //         this.noDevice = true;
                    //         this.loading = false;
                    //     }
                    // );
                } else {
                    // this.meetingSession.audioVideo.stopLocalVideoTile();
                }
            }
        }
    }

    // choose device for video input
    selectCamera($event) {
        // stop preview from old camera resource
        this.stopVideoPreview();
        this.meetingSession.audioVideo.stopLocalVideoTile();
        let device = $event.detail.value;
        this.meetingSession.audioVideo.startVideoInput(device.deviceId).then(
            (success) => {
                this.selectedVideoInputDevice = device;
                this.videoInput = device;
                // start stream
                this.meetingSession.audioVideo.startLocalVideoTile();
                // start camera preview
                this.startVideoPreview();
            }, (error) => {
                this.overlayService.showError(this.translate.instant('VIDEOCALL_DEVICE_ERROR', { device: device.label }))
            }
        );
    }

    // choose device for audio input
    selectMicrophone($event) {
        let device = $event.detail.value;
        this.meetingSession.audioVideo.startAudioInput(device.deviceId).then(
            (success) => {
                this.selectedAudioInputDevice = device;
                this.audioInput = device;
                this.startAudioPreview();
            }, (error) => {
                this.overlayService.showError(this.translate.instant('VIDEOCALL_DEVICE_ERROR', { device: device.label }))
            }
        );
    }

    // choose device for audio output
    selectSpeakers($event) {
        let device = $event.detail.value;
        if (this.defaultBrowserBehaviour.supportsSetSinkId()) {
            this.meetingSession.audioVideo.chooseAudioOutput(device.deviceId).then(
                (success) => {
                    this.selectedAudioOutputDevice = device;
                    this.audioOutput = device;
                    // bind audio only if output is enabled
                    if (this.audioEnabled) {
                        this.meetingSession.audioVideo.bindAudioElement(this.audioElement).then(

                            (success) => {
                                log('info', 'Sound:', this.audioOutput);
                            });
                    }
                }, (error) => {
                    this.overlayService.showError(this.translate.instant('VIDEOCALL_DEVICE_ERROR', { device: device.label }))
                }
            );
        }
    }

    // enable/disable screen sharing
    toggleContentShare() {
        this.contentShared = !this.contentShared;
        if (this.contentShared) {
            try {
                this.meetingSession.audioVideo.startContentShareFromScreenCapture().then(
                    (success) => {
                        // share started
                    },
                    (error) => {
                        // share was not started
                        this.contentShared = false;
                        if (error.message != 'Permission denied') {
                            this.noScreenShare = true;
                            this.overlayService.showError(this.translate.instant('SCREEN_CAPTURING_IS_NOT_AVAILABLE'));
                        }
                    });
            } catch (e) {
                // share was not started
                this.contentShared = false;
            }
        } else {
            this.meetingSession.audioVideo.stopContentShare();
        }
    }

    // play some test sound to verify speakers functionlity
    testSound(
        sinkId: string | null,
        frequency: number = 440,
        durationSec: number = 1,
        rampSec: number = 0.1,
        maxGainValue: number = 0.1
    ) {
        if (!this.testingSound) {
            this.testingSound = true;
            try {
                // @ts-ignore
                let audioContext: AudioContext = new (window.AudioContext || window.webkitAudioContext)();
                let gainNode = audioContext.createGain();
                gainNode.gain.value = 0;
                let oscillatorNode = audioContext.createOscillator();
                oscillatorNode.frequency.value = frequency;
                oscillatorNode.connect(gainNode);
                let destinationStream = audioContext.createMediaStreamDestination();
                gainNode.connect(destinationStream);
                let currentTime = audioContext.currentTime;
                let startTime = currentTime + 0.1;
                gainNode.gain.linearRampToValueAtTime(0, startTime);
                gainNode.gain.linearRampToValueAtTime(maxGainValue, startTime + rampSec);
                gainNode.gain.linearRampToValueAtTime(maxGainValue, startTime + rampSec + durationSec);
                gainNode.gain.linearRampToValueAtTime(0, startTime + rampSec * 2 + durationSec);
                oscillatorNode.start();
                let audioMixController = new DefaultAudioMixController();
                if (this.defaultBrowserBehaviour.supportsSetSinkId() && this.selectedAudioOutputDevice) {
                    try {
                        // @ts-ignore
                        audioMixController.bindAudioDevice({ deviceId: this.selectedAudioOutputDevice.deviceId });
                    } catch (e) {
                        log('error', `Failed to bind audio device: ${e}`);
                    }
                }
                audioMixController.bindAudioElement(new Audio());
                audioMixController.bindAudioStream(destinationStream.stream);
                new TimeoutScheduler((rampSec * 2 + durationSec + 1) * 1000).start(() => {
                    audioContext.close();

                    // bind again normal audio output for speakers,
                    // otherwise it can happen that it will disable audio output
                    if (this.audioEnabled) {
                        this.meetingSession.audioVideo.unbindAudioElement();
                        setTimeout(() => {
                            this.testingSound = false;
                            // if supported set some device
                            if (this.defaultBrowserBehaviour.supportsSetSinkId() && this.selectedAudioOutputDevice) {
                                this.meetingSession.audioVideo.chooseAudioOutput(this.selectedAudioOutputDevice.deviceId).then(
                                    (success) => {
                                        log('info', 'Sound: device', success);
                                        this.meetingSession.audioVideo.bindAudioElement(this.audioElement).then(
                                            (success) => {
                                                log('info', 'Sound:', success);
                                            }, (error) => {
                                                log('error', 'Sound:', error);
                                            });
                                    }, (error) => {
                                        log('error', 'Sound:', error);
                                        this.overlayService.showError(this.translate.instant('VIDEOCALL_DEVICE_ERROR', { device: this.selectedAudioOutputDevice.label }))
                                    }
                                );
                            } else {
                                this.meetingSession.audioVideo.bindAudioElement(this.audioElement).then(
                                    (success) => {
                                        log('info', 'Sound:', success);
                                    }, (error) => {
                                        log('error', 'Sound:', error);
                                    });
                            }

                        }, 500);
                    } else {
                        setTimeout(() => {
                            this.testingSound = false;
                        }, 500);
                    }
                });
            } catch (e) {
                log('error', 'failed to chooseAudioOutputDevice for sound test', e);
            }
        }
    }

    // start audio preview for microphone
    startAudioPreview() {
        this.setAudioPreviewPercent(0);

        // Recreate.
        if (this.analyserNode) {
            // Disconnect the analyser node from its inputs and outputs.
            this.analyserNode.disconnect();
            this.analyserNode.removeOriginalInputs();

            this.analyserNode = undefined;
        }

        const analyserNode = this.meetingSession.audioVideo.createAnalyserNodeForAudioInput();

        if (!analyserNode) {
            return;
        }

        if (!analyserNode.getByteTimeDomainData) {
            document.getElementById('audio-preview').parentElement.style.visibility = 'hidden';
            return;
        }

        this.analyserNode = analyserNode;
        const data = new Uint8Array(analyserNode.fftSize);
        let frameIndex = 0;
        this.analyserNodeCallback = () => {
            if (frameIndex === 0) {
                analyserNode.getByteTimeDomainData(data);
                const lowest = 0.01;
                let max = lowest;
                for (const f of data) {
                    max = Math.max(max, (f - 128) / 128);
                }
                let normalized = (Math.log(lowest) - Math.log(max)) / Math.log(lowest);
                let percent = Math.min(Math.max(normalized * 100, 0), 100);
                this.setAudioPreviewPercent(percent);
            }
            frameIndex = (frameIndex + 1) % 2;
            requestAnimationFrame(this.analyserNodeCallback);
        };
        requestAnimationFrame(this.analyserNodeCallback);
    }

    // end audio preview for microphone
    stopAudioPreview() {
        if (!this.analyserNode) {
            return;
        }

        this.analyserNodeCallback = () => { };

        // Disconnect the analyser node from its inputs and outputs.
        this.analyserNode.disconnect();
        this.analyserNode.removeOriginalInputs();

        this.analyserNode = undefined;
    }

    startVideoPreview(elm = 'video-preview') {
        let previewElement = document.getElementById(
            elm
        ) as HTMLVideoElement;
        if (previewElement) {
            this.meetingSession.audioVideo.startVideoPreviewForVideoInput(previewElement);
        }
    }

    async stopVideoPreview(elm = 'video-preview') {
        let previewElement = document.getElementById(
            elm
        ) as HTMLVideoElement;
        if (previewElement) {
            this.meetingSession.audioVideo.stopVideoPreviewForVideoInput(previewElement);
            await this.meetingSession.audioVideo.stopVideoInput();
        }
    }

    // show microphone preview in progressbar
    setAudioPreviewPercent(percent: number): void {
        const audioPreview = document.getElementById('audio-preview');
        if (audioPreview && audioPreview.getAttribute('aria-valuenow') !== `${percent}`) {
            audioPreview.style.width = `${percent}%`;
            audioPreview.setAttribute('aria-valuenow', `${percent}`);
        }
        const transitionDuration = '33ms';
        if (audioPreview && audioPreview.style.transitionDuration !== transitionDuration) {
            audioPreview.style.transitionDuration = transitionDuration;
        }
    }

    // close call
    endCall() {

        if (this.meetingSession) {
            this.stopVideoStreams();
        }

        // end call
        this.overlayService.endCall(this.chimeMeetingId);

        setTimeout(() => {
            this.onCanceled.emit();
        }, 100);
    }


    // when meeting has ended, show info about it
    public async meetingEnded() {

        // close video call
        this.onCanceled.emit();

        // inform user
        let alert = await this.alertCtrl.create({
            header: this.translate.instant('VIDEOCALL_HAS_ENDED'),
            message: this.translate.instant('VIDEOCALL_HAS_ENDED_EXPIRED'),
            buttons: [this.translate.instant('OK')]
        });

        await alert.present();
    }

    // stop all content capturing on users side
    public async stopVideoStreams() {

        // Stop local video tile (stops sharing the video tile in the meeting)
        this.meetingSession.audioVideo.stopLocalVideoTile();

        await this.meetingSession.audioVideo.startVideoInput(null);

        // stop video preview
        this.stopVideoPreview();

        // stop screen sharing
        this.meetingSession.audioVideo.stopContentShare();

        this.meetingSession.audioVideo.stop();
    }

    // handle active speaker callback
    public activeSpeakerCallback = (attendeeIds) => {
        if (attendeeIds.length) {
            log('info', `${attendeeIds[0]} is the most active speaker`);
            // find video stream if available, or put attendee to main window
            this.attendeePresenceSet.forEach((externalUserId) => {
                if (this.roster[externalUserId] && this.roster[externalUserId].chimeAttendeeId == attendeeIds[0]) {
                    this.activeSpeaker = externalUserId;
                    if (!this.attendeeVisibleSet.has(externalUserId)) {
                        if ((this.attendeeVisibleSet.size + 1) >= this.streamLimits) {
                            // remove some other participant from visibles
                            let last = null;
                            this.attendeeVisibleSet.forEach((key) => {
                                last = key;
                            });
                            // hide one participant from boxes on left
                            log('info', `${last} remove from visible`);
                            this.attendeeVisibleSet.delete(last);
                            this.roster[last].visible = false;
                        }

                        this.attendeeVisibleSet.add(externalUserId);
                        this.roster[externalUserId].visible = true;
                    }
                }
            });
        }
    };

    // assign video element to new video stream or check if exising
    public acquireVideoElement(tileState) {

        this.videoStreams[tileState.boundExternalUserId] = tileState;
        // Return the same video element if already bound.
        if (document.getElementById(tileState.boundExternalUserId)) {
            return <HTMLVideoElement>document.getElementById(tileState.boundExternalUserId);
        }

        throw new Error('no video element is available');
    };

    // remove video stream from list
    public releaseVideoElement(tileId) {
        // find participatn id by stream id
        let videoStream = Object.keys(this.videoStreams).forEach((key) => {
            if (this.videoStreams[key].tileId == tileId) {
                delete this.videoStreams[key];
                return;
            }
        });
    };

    public tryToAddUser(externalUserId, attendeeId) {
        // add user to and to list
        let attendee = this.chatGroupAttendees.find((attendee) => this.chimeParticipantPrefix + attendee.id == externalUserId);
        if (attendee) {
            this.addNewParticipantToCall(externalUserId, attendeeId, attendee);
        } else {
            this.loadAttendeeDetails(externalUserId, attendeeId);
        }
    }

    public loadAttendeeDetails(externalUserId, attendeeId, tryCount = 0) {
        tryCount++;
        this.participantService.getById(parseInt(externalUserId.replace(this.chimeParticipantPrefix, ''))).subscribe(
            (participant) => {
                if (participant.id) {
                    this.addNewParticipantToCall(externalUserId, attendeeId, participant);
                }
            },
            (error) => {
                // user data was not loaded, try it again after some time, max x times...
                if (tryCount < this.reconnectTryCount) {
                    setTimeout(() => {
                        this.loadAttendeeDetails(externalUserId, attendeeId, tryCount);
                    }, this.reconnectTryCount * 500);
                } else {
                    // NOTE[jg] - should we do something?
                }
            });
    }

    // add all details about participatn to roster and users list
    public addNewParticipantToCall(externalUserId, attendeeId, participant) {
        // is user box visible
        // only if there is a place on left side
        if (this.attendeeVisibleSet.size < this.streamLimits) {
            this.attendeeVisibleSet.add(externalUserId);
        }

        if (this.roster.hasOwnProperty(externalUserId)) {
            this.roster[externalUserId].chimeAttendeeId = attendeeId;
            this.roster[externalUserId].attendee = new Participant(participant);
        } else {

            this.roster[externalUserId] = {
                chimeAttendeeId: attendeeId,
                attendee: new Participant(participant),
                volume: null,
                muted: null,
                signalStrength: null,
                visible: this.attendeeVisibleSet.has(externalUserId)
            };
        }

        this.publishAttendees();

        this.attendeePresenceSet.add(externalUserId);
        if (!this.activeSpeaker) {
            this.activeSpeaker = externalUserId;
        }
    }

    // add new participant to box for visibles on left side from full list
    public addVisibleAttendee(updateSpeaker: boolean = false) {

        if (updateSpeaker) {
            this.activeSpeaker = null;
        }

        // first check those with videos
        this.attendeePresenceSet.forEach((externalUserId) => {
            if (this.attendeeVisibleSet.size < this.streamLimits) {
                if (!this.attendeeVisibleSet.has(externalUserId) && this.videoStreams[externalUserId]) {
                    this.attendeeVisibleSet.add(externalUserId);
                    this.roster[externalUserId].visible = true;
                }
            }
            if (updateSpeaker && this.videoStreams[externalUserId]) {
                this.activeSpeaker = externalUserId;
            }
        });
        if (this.attendeeVisibleSet.size < this.streamLimits) {
            this.attendeePresenceSet.forEach((externalUserId) => {
                if (!this.attendeeVisibleSet.has(externalUserId)) {
                    this.attendeeVisibleSet.add(externalUserId);
                    this.roster[externalUserId].visible = true;
                    if (updateSpeaker) {
                        this.activeSpeaker = externalUserId;
                    }
                }
                if (updateSpeaker && !this.activeSpeaker) {
                    this.activeSpeaker = externalUserId;
                }
            });
        }

        log('info', `dropped speaker ${this.activeSpeaker}`);
    }

    // refresh page
    retry() {
        document.location.reload();
    }

    /**
     * function to publish actual video call attendee list
     */
    publishAttendees() {
        let attendees = Object.values(this.roster).map(
            item => item['attendee']
        ).filter(
            item => item.id != this.user.selected_participant_id
        );

        this.appEvents.publish('call:attendees', attendees);
    }

    /**
     * function to publish open video call attendee list request
     */
    publishOpenAttendees() {
        this.appEvents.publish('call:attendees:open');
    }

    /**
     * Watch changes on Inputs and refresh component if needed
     *
     * @param participant
     */
    ngOnChanges(changes: SimpleChanges) {

        if (
            changes.hasOwnProperty('attendeeId')
            && changes.attendeeId.previousValue != undefined
        ) {
            // console.info(this.chatGroupAttendees);
        }
    }

    ngOnDestroy() {
        // end call, allow to create again notification about waiting call
        this.overlayService.endCall(this.chimeMeetingId);

        // remove notification about call for other participant
        if (this.notification) {
            this.feedService.decline(this.notification).subscribe(
                (success) => {
                    // nothing, notification was for other part
                },
                (error) => {
                    // nothing, notification was for other part
                }
            );
        }

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();

        // disable input devices - camera/mic
        if (this.meetingSession) {

            this.stopVideoStreams();
        }
    }

}
